/**
 * Cast4all PV production test composable.
 *
 * Starts a test on the meter's communicator and polls its result until Cast4all marks it final
 * (about 3.5 minutes). State is kept per serial number, so a running test keeps going and keeps
 * its result when the user switches to another meter and back.
 */

import { ref, computed } from 'vue'
import fetchData from '@/util/fetch'
import { notifications } from '@/util'

const POLL_INTERVAL_MS = 10000
// Cast4all needs about 3.5 minutes; stop polling well after that
const MAX_WAIT_MS = 8 * 60 * 1000

export function usePvProductionTest(serialNumber) {
  // { [serial]: { running, testId, result, error } }
  const testsBySerial = ref({})

  const pvTest = computed(
    () => testsBySerial.value[serialNumber.value] ?? { running: false, testId: null, result: null, error: null }
  )

  const update = (serial, patch) => {
    testsBySerial.value = {
      ...testsBySerial.value,
      [serial]: { ...(testsBySerial.value[serial] ?? {}), ...patch },
    }
  }

  const poll = async (serial, testId, startedAt) => {
    try {
      const result = await fetchData(`/eleena/api/v1/meters/${serial}/c4a/pv-production-test/${testId}`)
      const test = result?.data ?? null
      update(serial, { result: test })
      if (test?.final) {
        update(serial, { running: false })
        return
      }
    } catch (e) {
      // a single failed poll is not the end of the test, Cast4all is still running it
      console.error('PV production test poll failed', e)
    }
    if (Date.now() - startedAt > MAX_WAIT_MS) {
      update(serial, { running: false, error: 'Cast4all heeft de test na 8 minuten nog niet afgerond' })
      return
    }
    setTimeout(() => poll(serial, testId, startedAt), POLL_INTERVAL_MS)
  }

  const startPvProductionTest = async () => {
    const serial = serialNumber.value
    if (testsBySerial.value[serial]?.running) return
    if (!window.confirm(`Productietest starten op meter ${serial} via Cast4all? Dit duurt ca. 3-4 minuten.`)) return
    update(serial, { running: true, testId: null, result: null, error: null })
    try {
      const result = await fetchData(`/eleena/api/v1/meters/${serial}/c4a/pv-production-test`, { method: 'POST' })
      const testId = result?.data?.test_id
      if (!testId) throw new Error(result?.message || 'Cast4all gaf geen test-id terug')
      update(serial, { testId })
      setTimeout(() => poll(serial, testId, Date.now()), POLL_INTERVAL_MS)
    } catch (e) {
      // fetchData throws the API's JSON body: our endpoints send `message`, FastAPI's own errors `detail`
      const reason = e?.message || (typeof e?.detail === 'string' ? e.detail : null)
      update(serial, { running: false, error: reason || 'onbekende fout' })
      notifications.addNotification({ message: `Productietest starten mislukt: ${reason || 'onbekende fout'}`, type: 'danger' })
    }
  }

  return { pvTest, startPvProductionTest }
}
